<template>
  <v-container class="py-5 px-5" fluid v-if="this.$store.state.signedIn">
      <v-layout wrap justify-center>
            <v-flex xs12 md8 class="px-2 py-2">
                <v-toolbar color="secondary" dark> 
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Search an ASN for a breakdown of it's involvement in BGP anomalies
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <info>
                        <template>
                            ASN Baseline View
                        </template>
                        <template slot="body">
                            <p> ASN baseline view gives an overview of the selected ASN's history within our system.</p>
                            <p> There are four graphs to aid understanding:</p>
                            <ul>
                                <li> ASN country breakdown. Note some countries shown in the key may not appear in the pie-chart, meaning they have less than a 0.01% proportion of the address space. (top left)</li>
                                <li> Mean Impact and Hijack score of episodes that the selected ASN has been involved in. Displayed in a histogram format. (top right)</li>
                                <li> Combined mean impact and hijack score for episodes that the selected ASN has been involved in. Displayed in a histogram format (bottom left)</li>
                                <li> A count of the event types that this ASN has been involved in (bottom right).</li>
                            </ul>
                            <br>
                            <p> All graphs are calculated based on ASN activity within the last day, or longer if specified.</p>
                            <br />
                            <p> <b>The ASN country breakdown is calculated as follows:</b>
                                <ol>
                                    <li> All prefixes from internet registries (RIPE, APNIC, AFRINIC, LACNIC, LEVEL3) that have this ASN as the owner are stored. </li>
                                    <li> For each of those prefixes, we then take all prefixes with geographic (country) data associated with them, and perform the following aggregation:
                                        <ol type="a">
                                            <li> If we have a direct match, take this as the (only) country information for prefix. </li>
                                            <li> Else, consider all child prefixes (more specific e.g. a /24 child of a /16 prefix), and aggregate upwards.
                                            <i> e.g. A /24 child prefix of a /23 prefix would account for half (1/2) of the IP space </i> </li>
                                            <li> Any remaining space is accounted for by either a parent prefix in registry data (e.g. a /10 parent of a /16 prefix), 
                                                or if there are no parent prefixes, the country where the ASN is registered. </li>
                                        </ol>
                                    </li>
                                    <li> Once we have a geographical breakdown for each prefix owned by an ASN (<b>These are visible in the table above</b>), we then aggregate these again as follows:
                                        <ol type="a">
                                            <li> The geographical breakdown of a given prefix is multiplied by the <b>CIDR (the number after the '/' on a prefix)</b> as an inverse power of 2. This is best explained by comparing an example (the maths is a little hard to understand). </li>
                                            <li> Let's say we have only a /32 prefix and a /31 prefix (both in IPv4 - we will come on to IPv6 later on). The /31 has twice as much IP space as the /32, so accounts for twice as much of the ASN aggregation. </li>
                                            <li> A more polarising example is if we have only a /16 prefix and a /32 prefix. Now the /16 has 2^16 = 65,536 times as much IP space as the /32, hence the /32 is almost insignificant. It is for this reason we often end up with a lot of 'Additional Countries' listed at the bottom of the geographical breakdown. </li>
                                            <li> Where we have an ASN with both IPv4 and IPv6 prefixes, we multiply the IPv4 CIDRs by 4 (since the max of IPv4 CIDRs is 32 and IPv6 is 128, 128/4 = 32). For example, a /16 IPv4 prefix is equivalent to a /48 IPv6 prefix in the calculation.</li>
                                        </ol>
                                    </li>
                                    <li> <b>This is updated daily</b> </li>
                                </ol>
                            </p>
                        </template>
                    </info>
                </v-toolbar>
                <v-card>
                    <v-card-text :style="$store.state.textFormatting.main">
                        <v-form ref="form" v-model="valid" lazy-validation>
                            <v-layout wrap>
                                <v-flex xs12 md6>
                                    <v-text-field label="ASN" v-model="ASN" :rules="asn_rules" required :style="$store.state.textFormatting.main" @keyup.enter.prevent="getBaseLineInformation(ASN, timeScale)"> </v-text-field>
                                </v-flex>
                                <v-flex xs12 md4 class="px-2">
                                    <v-select :items="timeScales" v-model="timeScale" label="Time Scale" required :style="$store.state.textFormatting.main"> </v-select>
                                </v-flex>
                                <v-flex xs12 md2>
                                    <v-btn color="success" dark @click="getBaseLineInformation(ASN, timeScale)" :disabled="!valid" :style="$store.state.textFormatting.main"> Submit </v-btn>
                                </v-flex>
                            </v-layout>
                        </v-form>
                        
                        <v-flex v-if="displayMessage">
                            <v-card class="justify-right" color="#ffbf00">
                                <h3 :style="$store.state.textFormatting.main"> No ASN baseline information for {{ searchedASN }} </h3>
                            </v-card>
                        </v-flex>
                    </v-card-text>
                </v-card> 
            </v-flex>
      </v-layout>
      <v-layout>
            <v-flex xs12 md8 class="px-2 py-2">
                <v-toolbar dark color="secondary" v-if="toolbar_show.main">
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        ASN Baseline View: {{ searchedASN }}
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <info>
                        <template>
                            ASN Baseline View
                        </template>
                        <template slot="body">
                            <p> ASN baseline view gives an overview of the selected ASNs history within our system.</p>
                            <p> There are four graphs to aid understanding:</p>
                            <ul>
                                <li> ASN country breakdown. Note some countries shown in the key may not appear in the pie-chart, meaning they have less than a 0.01% proportion of the address space. (top left)</li>
                                <li> Mean Impact and Hijack score of episodes that the selected ASN has been involved in. Displayed in a histogram format. (top right)</li>
                                <li> Combined mean impact and hijack score for episodes that the selected ASN has been involved in. Displayed in a histogram format (bottom left)</li>
                                <li> A count of the event types that this ASN has been involved in (bottom right).</li>
                            </ul>
                            <br>
                            <p> All graphs are calculated based on ASN activity within the last day, or longer if specified.</p>
                            <br />
                            <p> <b>The ASN country breakdown is calculated as follows:</b>
                                <ol>
                                    <li> All prefixes from internet registries (RIPE, APNIC, AFRINIC, LACNIC, LEVEL3) that have this ASN as the owner are stored. </li>
                                    <li> For each of those prefixes, we then take all prefixes with geographic (country) data associated with them, and perform the following aggregation:
                                        <ol type="a">
                                            <li> If we have a direct match, take this as the (only) country information for prefix. </li>
                                            <li> Else, consider all child prefixes (more specific e.g. a /24 child of a /16 prefix), and aggregate upwards.
                                            <i> e.g. A /24 child prefix of a /23 prefix would account for half (1/2) of the IP space </i> </li>
                                            <li> Any remaining space is accounted for by either a parent prefix in registry data (e.g. a /10 parent of a /16 prefix), 
                                                or if there are no parent prefixes, the country where the ASN is registered. </li>
                                        </ol>
                                    </li>
                                    <li> Once we have a geographical breakdown for each prefix owned by an ASN (<b>These are visible in the table above</b>), we then aggregate these again as follows:
                                        <ol type="a">
                                            <li> The geographical breakdown of a given prefix is multiplied by the <b>CIDR (the number after the '/' on a prefix)</b> as an inverse power of 2. This is best explained by comparing an example (the maths is a little hard to understand). </li>
                                            <li> Let's say we have only a /32 prefix and a /31 prefix (both in IPv4 - we will come on to IPv6 later on). The /31 has twice as much IP space as the /32, so accounts for twice as much of the ASN aggregation. </li>
                                            <li> A more polarising example is if we have only a /16 prefix and a /32 prefix. Now the /16 has 2^16 = 65,536 times as much IP space as the /32, hence the /32 is almost insignificant. It is for this reason we often end up with a lot of 'Additional Countries' listed at the bottom of the geographical breakdown. </li>
                                            <li> Where we have an ASN with both IPv4 and IPv6 prefixes, we multiply the IPv4 CIDRs by 4 (since the max of IPv4 CIDRs is 32 and IPv6 is 128, 128/4 = 32). For example, a /16 IPv4 prefix is equivalent to a /48 IPv6 prefix in the calculation.</li>
                                        </ol>
                                    </li>
                                    <li> <b>This is updated daily</b> </li>
                                </ol>
                            </p>
                        </template>
                        <template slot="action">
                            <v-btn flat color="primary" @click="routeSearchEpisodes" :style="$store.state.textFormatting.main"> View more episodes</v-btn>
                        </template>
                    </info>
                </v-toolbar>
                <v-card>
                    <v-progress-circular v-if="loading" indeterminate color="purple" />
                    <v-card-text v-if="isCountrydata">
                        <v-layout wrap>
                                <v-flex xs12 md6>
                                    <v-flex xs12>
                                        <doughnut :chart-data="doughnutData" :options="doughnutoptions"> </doughnut>
                                    </v-flex>
                                    <v-flex xs12 v-if="additional_countries.length > 0">
                                        <v-card>
                                            <v-card-text :style="$store.state.textFormatting.main">
                                                <h2 :style="$store.state.textFormatting.headers">
                                                    Additional Countries (&lt;0.005%):
                                                </h2>
                                                {{ additional_countries }}
                                            </v-card-text>
                                        </v-card>
                                    </v-flex>
                                </v-flex>
                                <v-flex xs12 md6>
                                    <v-card v-if="!isEventData">
                                        <v-card-text :style="$store.state.textFormatting.main"> No Event Data for this ASN over the last {{ timeScale }}. The bar charts therefore will not render. </v-card-text>
                                    </v-card>
                                    <v-flex xs12 v-if="isEventData">
                                        <barchart :chart-data="barchartData[0]" :options="options"> </barchart>
                                    </v-flex>
                                    <v-flex xs12 v-if="isEventData">
                                        <barchart :chart-data="barchartData[1]" :options="options"> </barchart>
                                    </v-flex>
                                    <v-flex xs12 v-if="isEventData">
                                        <barchart :chart-data="barchartData[2]" :options="options"> </barchart>
                                    </v-flex>
                                </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </v-flex>
            
            <v-flex xs12 md4 class="px-2 py-2">
                <v-toolbar dark flat color="secondary" height="96x" v-if="toolbar_show.prefixes">
                    <v-toolbar-title :style="$store.state.textFormatting.headers">
                        Selected ASN: {{ searchedASN }} <br> <font  :style="$store.state.textFormatting.main"> {{ asnorg }} </font> <v-progress-circular v-if="loading" icon indeterminate color="blue" ></v-progress-circular>
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <downloadcsv v-if="!show_current"
                        class   = "btn btn-default"
                        :data   = "ASNEnrichData"
                        :name = "regCSVName">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Ref. Prefixes </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">Prefixes listed as owned by this ASN in registry data (see info.)</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <downloadcsv v-if="show_current"
                        class   = "btn btn-default"
                        :data   = "livePrefixes"
                        :name = "regCSVName_2">
                        <v-tooltip top eager color="primary" id="exit-app">
                            <template v-slot:activator="{ on }">
                                <v-btn small dark v-on="on" color="success" :style="$store.state.textFormatting.main"> <v-icon>save_alt</v-icon> Live Prefixes </v-btn>
                            </template>
                            <span :style="$store.state.textFormatting.main">Prefixes currently advertised by this ASN and whether they're owned by this ASN in registry data</span>
                        </v-tooltip>        
                    </downloadcsv>
                    <info>
                        <template>
                            List of ASN Prefixes
                        </template>
                        <template slot="body">
                            <p>A list of prefixes which have been registered with the ASN as per information gathered by
                            <b> Internet Routing Registries. </b>
                            </p>
                            <br>
                            <p> The country breakdown gives the percentage of IP addresses under the prefix which reside in each country where 0% = >0.01% </p>
                            <br />
                            <p> <b>The country breakdown for each prefix is calculated as follows:</b>
                                <ol>
                                    <li> All prefixes from internet registries (RIPE, APNIC, AFRINIC, LACNIC, LEVEL3) that have this ASN as the owner are stored. </li>
                                    <li> For each of those prefixes, we then take all prefixes with geographic (country) data associated with them, and perform the following aggregation:
                                        <ol type="a">
                                            <li> If we have a direct match, take this as the (only) country information for prefix. </li>
                                            <li> Else, consider all child prefixes (more specific e.g. a /24 child of a /16 prefix), and aggregate upwards.
                                            <i> e.g. A /24 child prefix of a /23 prefix would account for half (1/2) of the IP space </i> </li>
                                            <li> Any remaining space is accounted for by either a parent prefix in registry data (e.g. a /10 parent of a /16 prefix), 
                                                or if there are no parent prefixes, the country where the ASN is registered. </li>
                                        </ol>
                                    </li>
                                    <li> <b>This is updated daily</b> </li>
                                </ol>
                            </p>
                        </template>
                    </info>
                </v-toolbar>
                <v-card v-if="loading2">
                    <v-flex xs4 class="px-2 py-2">    
                        <v-progress-circular indeterminate color="purple"></v-progress-circular>
                    </v-flex>
                </v-card>
                <v-card v-if="!loading2&&!showPrefixes&&toolbar_show.prefixes">
                    <v-card-text :style="$store.state.textFormatting.main">    
                        Couldn't load prefixes for this ASN
                    </v-card-text>
                </v-card>
                <v-card v-if="!loading2&&showPrefixes&&!show_current">
                    <v-layout justify-center align-center>
                        <v-flex class="px-2 py-2">
                            <v-btn color="success" @click="show_current=!show_current" :style="$store.state.textFormatting.main"> Show Currently Advertised Prefixes </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-layout v-if="updatedStr">
                        <v-card-text class="text-xs-center" :style="$store.state.textFormatting.main">
                            Prefixes from registry data, system last updated at{{ updatedStr }}
                        </v-card-text>
                    </v-layout>
                    <v-data-table dense :headers="headersPrefixLocation" :items="ASNEnrichData"
                    :rows-per-page-items="rowsPerPageItems" :pagination.sync="pagination" >
                        <template slot="headerCell" slot-scope="props">
                            <v-tooltip top eager>
                            <span slot="activator" :style="$store.state.textFormatting.main">
                                {{ props.header.text }}
                            </span>
                            <span :style="$store.state.textFormatting.main">
                                {{ props.header.ad_text }}
                            </span>
                            </v-tooltip>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr>
                                <td class="text-xs-left" :style="$store.state.textFormatting.main"> {{ props.item.prefix }}</td>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.country }}</td>
                                <td class="text-xs-center" :style="$store.state.textFormatting.main"> {{ props.item.source }}</td>
                            </tr>
                        </template>
                        <template slot="no-data">
                            <v-alert :key="id" :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                Prefix data missing
                            </v-alert>
                        </template>
                    </v-data-table>
                    <!-- <v-btn color="success" @click="next100Prefs()" :disabled="next100disabled"> Next 500 prefixes </v-btn>
                    <v-flex>
                        <h6> currently showing {{ lowpre }}-{{ highpre }} of {{ totalpre }} prefixes </h6>
                    </v-flex> !-->
                </v-card>
                <v-card v-if="live_loading&&show_current">
                    <v-layout justify-center align-center>
                        <v-flex class="px-2 py-2" justify-center align-center>
                            <v-btn color="success" @click="show_current=!show_current" :style="$store.state.textFormatting.main"> Show Ref. Data Prefixes </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-flex xs4 class="px-2 py-2">    
                        <v-progress-circular indeterminate color="purple"></v-progress-circular>
                    </v-flex>
                </v-card>
                <v-card v-if="!live_loading&&show_live&&show_current">
                    <v-layout justify-center align-center>
                        <v-flex class="px-2 py-2" justify-center align-center>
                            <v-btn color="success" @click="show_current=!show_current" :style="$store.state.textFormatting.main"> Show Ref. Data Prefixes </v-btn>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-card-text class="text-xs-center" :style="$store.state.textFormatting.main">
                            Prefixes from live data. {{ live_str }}
                        </v-card-text>
                    </v-layout>
                    <v-data-table dense :headers="headersLivePrefixes" :items="livePrefixes"
                    :rows-per-page-items="rowsPerPageItems2" :pagination.sync="pagination2" >
                        <template slot="headerCell" slot-scope="props">
                            <v-tooltip top eager>
                            <span slot="activator" :style="$store.state.textFormatting.main">
                                {{ props.header.text }}
                            </span>
                            <span :style="$store.state.textFormatting.main">
                                {{ props.header.ad_text }}
                            </span>
                            </v-tooltip>
                        </template>
                        <template slot="items" slot-scope="props">
                            <tr>
                                <td class="text-xl-center" :style="$store.state.textFormatting.main"> {{ props.item.prefix }}</td>
                                <td class="text-xl-center" :style="$store.state.textFormatting.main"> {{ props.item.observed_time }}</td>
                                <td class="text-xl-center" :style="$store.state.textFormatting.main">  <v-icon> {{ props.item.ref_icon }} </v-icon> </td>
                            </tr>
                        </template>
                        <template slot="no-data">
                            <v-alert :key="id" :value="true" color="success" icon="warning" :style="$store.state.textFormatting.main">
                                Prefix data missing
                            </v-alert>
                        </template>
                    </v-data-table>
                    <!-- <v-btn color="success" @click="next100Prefs()" :disabled="next100disabled"> Next 500 prefixes </v-btn>
                    <v-flex>
                        <h6> currently showing {{ lowpre }}-{{ highpre }} of {{ totalpre }} prefixes </h6>
                    </v-flex> !-->
                </v-card>
            </v-flex>
      </v-layout>
  </v-container>
</template>

<script>

import { API, graphqlOperation } from 'aws-amplify';
import doughnut from '../../shared/developing/doughnut-graph.js';
import barchart from '../../shared/operational/BarChart.js';
import { countryNames } from '../../shared/operational/countrynames.js';
import info from '../../shared/operational/infopopout.vue';
import { findParentComponentByName } from '../utils/findParent.js'
import downloadcsv from 'vue-json-csv';


export default {
    name: 'asn-baseline.vue',
    components: {
        doughnut,
        barchart,
        info,
        downloadcsv
    },
    data () {
        return {
            message: "",
            valid: false,
            headersPrefixLocation: [
                { text: 'Prefix', value:'prefix', align:'centre', ad_text: 'Prefix owned by ASN'},
                { text: 'Country', value:'country', align:'centre', ad_text: 'Breakdown of country information in registry data'},
                { text: 'Source', value:'source', align:'centre', ad_text: 'Which registry the ASN ownership information was acquired from'}
            ],
            headersLivePrefixes: [
                { text: 'Prefix', value:'prefix', align:'centre', ad_text: 'Prefix being advertised'},
                { text: 'Advertising Since', value:'observed_time', align:'centre', ad_text: 'When has this prefix been advertised since'},
                { text: 'In ref. data?', value:'ref_icon', align:'centre', ad_text: 'Is this prefix owned by the given ASN in registry data?'}
            ],
            loading:false,
            loading2:false,
            showPrefixes: false,
            asnorg: false,
            lowpre: 0,
            highpre: 500,
            totalpre: 0,
            next100disabled: false,
            ASNEnrichData: [],
            additional_countries: [],
            ASN: null,
            id: null,
            asn_rules: [ v => !!v || 'Please input an ASN', v => /(^$|^\d+$)/.test(v) || 'ASN must be non-negative integer'],
            size: "xs6",
            isCountrydata: false,
            isEventData: true,
            doughnutData: null,
            timeScale: "1 day",
            searchedASN: 0,
            selectedASN: null,
            barchartData: null,
            displayMessage: false,
            prefixrotation: 1,
            ownerData: [],
            doughnutoptions: {
                responsive: true,
                maintainAspectRatio: true,
                tooltips: {
                    titleFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10, 
                    bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                },
                legend: {
                    position: 'top',
                    labels: {
                        fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                    },
                },
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    titleFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10, 
                    bodyFontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                },
                legend: {
                    position: 'bottom',
                    labels: {
                        fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                    },
                },
                scales: {
                    yAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: 'Count',
                            fontSize: this.$store.state.textFormatting.traceroute.fontSize + 10
                        },
                        ticks: {
                            min: 0,
                            fontSize: this.$store.state.textFormatting.chart.axis_label
                        }
                    }],
                    xAxes: [{
                        scaleLabel: {
                            display: true,
                            labelString: '',
                        },
                        ticks: {
                            display: false
                        }
                    }]
                }
            },
            timeScales: [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31],
            rowsPerPageItems: [20, 30, 40, 50],
            pagination: {
                rowsPerPage: 20
            },
            rowsPerPageItems2: [20, 30, 40, 50],
            pagination2: {
                rowsPerPage: 20
            },
            updatedStr: false,
            livePrefixes: [],
            show_live: false,
            toolbar_show: {
                main: false,
                prefixes: false,
                live: false
            },
            live_loading: false,
            doneASN: 0,
            show_current: false,
            refs_ipv4: [],
            refs_ipv6: [],
            live_str: '',
            greenscale: [
                '#A4F483',
                '#6FDC41',
                '#41C409'
            ],
            bluescale: [
                '#7093F3',
                '#3B66DC',
                '#0C3EC5'
            ],
            purplescale: [
                '#CA98F6',
                '#A452ED',
                '#7D1AD6'
            ],
            col6: [
                '#41C409',
                '#0C3EC5',
                '#7D1AD6',
                '#D68A1A',
                '#BD1AD6',
                '#D61A4B',
                '#A5B48C'
            ],
            distinct_colors: [
                '#e6194b', 
                '#3cb44b', 
                '#ffe119', 
                '#4363d8', 
                '#f58231', 
                '#911eb4', 
                '#46f0f0', 
                '#f032e6', 
                '#bcf60c', 
                '#fabebe', 
                '#008080', 
                '#e6beff', 
                '#9a6324', 
                '#fffac8', 
                '#800000', 
                '#aaffc3', 
                '#808000', 
                '#ffd8b1', 
                '#000075', 
                '#808080', 
                //'#ffffff', remove white
                '#000000'
            ],
            regCSVName: '',
            regCSVName_2: ''
        }
    },
    beforeMount() {

        let app = findParentComponentByName(this, 'app')
        app.checkSignedIn()

        for (var i=0; i<this.timeScales.length; i=i+1){
            if (this.timeScales[i] == 1){
                this.timeScales[i] = this.timeScales[i] + " day"
            } else {
                this.timeScales[i] = this.timeScales[i] + " days"
            }
            
        }
        this.getUpdateTs()
        if (this.$store.state.searches.baseline) {
            this.ASN = this.$store.state.searches.baseline
        }
    },
    methods: {
        getUpdateTs: async function () {
            var qry = `query getUpdateTimestamp { getUpdateTimestamp ( table_name: "asn_country_breakdown" ) {
                    ts
                }
            }`;

            console.log(qry)
                    
            const tsdata = await API.graphql(graphqlOperation(qry));
            console.log(tsdata)
            if (tsdata) {
                if (tsdata.data.getUpdateTimestamp) {
                    var dd = new Date(tsdata.data.getUpdateTimestamp[0].ts * 1000);
                        
                    this.updatedStr = " " + dd.toISOString().slice(0,10) + " " + dd.toISOString().slice(11,16)
                }
            }
            
        },
        routeSearchEpisodes: function () {
            this.$router.push({ name: 'Search Events, Episodes or Notifications', 
                params: {asn: this.searchedASN}})
        },
        comparePrefixes: async function () {
            this.live_loading = true
            const t1 = Date.now()
            console.log("comparing prefixes")
            var count_good = 0
            var count_bad = 0
            for ( var i = 0 ; i < this.livePrefixes.length ; i++ ) {
                let l_pre = this.livePrefixes[i]
                l_pre.in_ref = false
                if ( l_pre.prefix.includes(":") ) {
                    if ( this.refs_ipv6.length > 0) {
                        for ( var j = 0 ; j < this.refs_ipv6.length ; j++ ) {
                            let m_pre = this.refs_ipv6[j]
                            if ( m_pre == l_pre.prefix ) {
                                l_pre.in_ref = true
                            }
                            if ( j == this.refs_ipv6.length - 1 ) {
                                if ( l_pre.in_ref ) {
                                    l_pre.ref_icon = "check";
                                    count_good += 1
                                } else {
                                    l_pre.ref_icon = "clear";
                                    count_bad += 1
                                }
                            }
                        }
                    } else {
                        l_pre.ref_icon = "clear";
                        count_bad += 1
                    }
                    
                } else {
                    if ( this.refs_ipv4.length > 0 ) {
                        for ( var j = 0 ; j < this.refs_ipv4.length ; j++ ) {
                            let m_pre = this.refs_ipv4[j]
                            if ( m_pre == l_pre.prefix ) {
                                l_pre.in_ref = true
                            }
                            if ( j == this.refs_ipv4.length - 1 ) {
                                if ( l_pre.in_ref ) {
                                    l_pre.ref_icon = "check";
                                    count_good += 1
                                } else {
                                    l_pre.ref_icon = "clear";
                                    count_bad += 1
                                }
                            }
                        }
                    } else {
                        l_pre.ref_icon = "clear";
                        count_bad += 1
                    }
                    
                }
                
                if ( i == this.livePrefixes.length - 1 ) {
                    this.live_loading = false
                    const t2 = Date.now()
                    console.log("comparison took " + (t2 - t1).toString() + " seconds")
                    this.live_str = count_good.toString() + " advertised prefixes in ref. data, " + count_bad.toString() + " not in ref. data (" + (Math.round(count_good/(count_good + count_bad) * 10000) / 100).toFixed(2).toString() + "% compliant)"
                }
            }
        },
        getLivePrefixes: async function () {
            var asn = this.searchedASN;
            this.live_loading = true
            var to_push = []
            try {
                var qry = `query getLivePrefixesForASN { getLivePrefixesForASN ( asn: "` + asn + `", type: 4 ) {
                        prefix
                        observed_owner_asn
                        observed_owner_asn_dt
                    }
                }`;
                console.log(qry)
                    
                const prefdata = await API.graphql(graphqlOperation(qry));
                if (prefdata.data.getLivePrefixesForASN) {
                    var prefs = prefdata.data.getLivePrefixesForASN;

                    console.log(prefs);
                    if (prefs.length > 0) {
                        for ( var i = 0 ; i < prefs.length ; i++ ) {
                            var dd = new Date(prefs[i].observed_owner_asn_dt * 1000);
                            prefs[i].observed_time = dd.toISOString().slice(0,10) + " " + dd.toISOString().slice(11,16)
                            to_push.push(prefs[i])
                        }
                    }
                }
            } catch (err) {
                console.log(err);
            }

            try {
                var qry = `query getLivePrefixesForASN { getLivePrefixesForASN ( asn: "` + asn + `", type: 6 ) {
                        prefix
                        observed_owner_asn
                        observed_owner_asn_dt
                    }
                }`;
                console.log(qry)
                    
                const prefdata = await API.graphql(graphqlOperation(qry));
                if (prefdata.data.getLivePrefixesForASN) {
                    var prefs = prefdata.data.getLivePrefixesForASN;

                    console.log(prefs);
                    if (prefs.length > 0) {
                        
                        
                        for ( var i = 0 ; i < prefs.length ; i++ ) {
                            var dd = new Date(prefs[i].observed_owner_asn_dt * 1000);
                            prefs[i].observed_time = dd.toISOString().slice(0,10) + " " + dd.toISOString().slice(11,16)
                            to_push.push(prefs[i])
                            if ( i == prefs.length - 1 ) {
                                this.livePrefixes = to_push
                                this.live_loading = false
                                this.show_live = true
                                if ( this.showPrefixes ) {
                                    if ( this.doneASN != this.searchedASN ) {
                                        this.doneASN = this.searchedASN
                                        this.comparePrefixes()
                                    }
                                }
                            }
                        }
                    } else {
                        this.livePrefixes = to_push
                        this.show_live = true
                        this.live_loading = false
                        if ( this.showPrefixes ) {
                            if ( this.doneASN != this.searchedASN ) {
                                this.doneASN = this.searchedASN
                                this.comparePrefixes()
                            }
                        }
                    }
                    
                } else {
                    this.livePrefixes = to_push
                    this.show_live = true
                    this.live_loading = false
                    if ( this.showPrefixes ) {
                        if ( this.doneASN != this.searchedASN ) {
                            this.doneASN = this.searchedASN
                            this.comparePrefixes()
                        }
                    }
                }
            } catch (err) {
                console.log(err);
                this.livePrefixes = to_push
                this.show_live = true
                this.live_loading = false
                if ( this.showPrefixes ) {
                    if ( this.doneASN != this.searchedASN ) {
                        this.doneASN = this.searchedASN
                        this.comparePrefixes()
                    }
                }
            }
        },
        getASNOwn: async function () {
            this.asnorg = null;
            this.totalpre = 0;
            this.showPrefixes = false;
            var asn = this.searchedASN;
            try {
                var qry = `query getASNOwner { getASNOwner ( asn: "` + asn + `" ) {
                        organisation
                    }
                }`;
                console.log(qry)
                    
                const ASNOwnData = await API.graphql(graphqlOperation(qry));
                if (ASNOwnData.data.getASNOwner) {
                    var ownData = ASNOwnData.data.getASNOwner;

                    console.log(ownData);
                    if (ownData.length > 0) {
                        this.ownerData = ownData;
                        this.asnorg = ownData[0].organisation;
                        
                        
                        /*var i;
                        for( i = 0; i < ownData.length; i++) {
                            this.totalpre = this.totalpre + ownData[i].totalprefs;

                        }*/
                    }
                }
            } catch (err) {
                console.log(err);
            }



        },
        next100Prefs: async function () {
            if(this.totalpre > this.highpre) {
                this.getPrefixInfo(this.searchedASN, this.prefixrotation);
                this.prefixrotation = this.prefixrotation + 1;
            } else {
                this.next100disabled = true;
            }


        },
        getPrefixInfo: async function(asn, rotation) {
            this.loading2 = true;
            this.lowpre = rotation * 500;
            this.highpre = rotation * 500 + 500;
            this.ASNEnrichData = [];
            this.refs_ipv4 = [];
            this.refs_ipv6 = [];
            this.showPrefixes = false
            this.show_live = false
            this.regCSVName = asn.toString() + "RegistryPrefixes.csv"
            this.regCSVName_2 = asn.toString() + "LivePrefixes.csv"
        
            try {
                if(rotation == 0) {
                    var getASNEnrichmentData = `query getASNEnrich {
                                                    getASNEnrich(asn: "`+asn+`", page_offset:0, page_rows:10000) {
                                                        asn
                                                        prefix
                                                        country
                                                        source
                                                    }
                    }`
                } else {
                    var getASNEnrichmentData = `query getASNEnrich {
                                                    getASNEnrich(asn: "`+asn+`", page_offset:` + 500 * rotation + `, page_rows:500) {
                                                        asn
                                                        prefix
                                                        country
                                                        source
                                                    }
                    }`
                }
                console.log(getASNEnrichmentData)
                
                const ASNEnrichmentData = await API.graphql(graphqlOperation(getASNEnrichmentData));
                console.log(ASNEnrichmentData)
                var ASNEnrichmentdataUnwraped = ASNEnrichmentData.data.getASNEnrich

                console.log(ASNEnrichmentdataUnwraped)
                var prefs = [];
                
                for(var count=0; count<ASNEnrichmentdataUnwraped.length; count=count+1){
                    if (ASNEnrichmentdataUnwraped[count].country == null) {
                        ASNEnrichmentdataUnwraped[count].country = "Unknown"
                    } else {
                         ASNEnrichmentdataUnwraped[count].country = this.sortPrefixCountry(JSON.parse(ASNEnrichmentdataUnwraped[count].country))
                    }
                    if (!prefs.includes(ASNEnrichmentdataUnwraped[count].prefix)) {
                        prefs.push(ASNEnrichmentdataUnwraped[count].prefix)
                    }
                    if (ASNEnrichmentdataUnwraped[count].prefix.includes(":")) {
                        this.refs_ipv6.push(ASNEnrichmentdataUnwraped[count].prefix)
                    } else {
                        this.refs_ipv4.push(ASNEnrichmentdataUnwraped[count].prefix)
                    }
                }
                this.totalpre = prefs.length;

                if (ASNEnrichmentdataUnwraped.length ==0) {
                    this.message="No information for this ASN"
                    this.showInfo = false
                    this.ASNEnrichData = []
                } else {
                    this.ASNEnrichData = ASNEnrichmentdataUnwraped;
                    this.showPrefixes = true;
                    if ( this.show_live ) {
                        if ( this.doneASN != this.searchedASN ) {
                            this.doneASN = this.searchedASN
                            this.comparePrefixes()
                        }
                    }
                    this.showInfo = true;
                    this.message='';
                }
                this.loading2 = false;
            } catch(err) {
                console.log(err)
                this.message=err.message
                this.loading2 = false;
            }
        },
        getBaseLineInformation: async function (asn, timeScale) {
            if(this.$refs.form.validate()) {
                this.displayMessage = false;
                this.show_live = false
                this.loading = true;
                this.next100disabled = false;
                this.isCountrydata = false;
                this.searchedASN = asn;
                this.toolbar_show.main = true;
                this.toolbar_show.prefixes = true;
                this.toolbar_show.live = true;
                this.$store.commit("setSearch", {'baseline': asn})
                this.getPrefixInfo(asn, 0)
                this.getASNOwn();
                this.getLivePrefixes();
                var timeScaleEpoch = timeScale.split(" ")[0] * 86400
                var query =` query getASNBaseLine { getASNBaseline (asn: "`+asn+`", interval_length: `+timeScaleEpoch+`, num_of_intervals: 1, page_offset: 0 , page_rows: 1) {
                    event_count
                    h_0_30
                    h_30_60
                    h_60_99
                    i_0_30
                    i_30_60
                    i_60_99
                    hi_0_900
                    hi_900_3600
                    hi_3600_9801
                    own_assert_count
                    first_obs_count
                    path_change_count
                    restricted_dns_count
                    bogon_count
                    route_leak_count
                    country_break_down
                }}`
                console.log(query);
                const ASNBaseLine = await API.graphql(graphqlOperation(query));
                var baseLineBreakDown = ASNBaseLine.data.getASNBaseline[0];
                console.log("baseline bd. below")
                console.log(baseLineBreakDown)
                if (baseLineBreakDown) {
                    if (baseLineBreakDown.country_break_down) {
                        var doughnutDataCountry = this.createdoughnutDataCountry(baseLineBreakDown.country_break_down);
                        this.isCountrydata = true;
                        this.doughnutData = doughnutDataCountry;
                        this.isEventData = true;
                        this.size="xs12";
                        var barChartData = this.createBarChartData(baseLineBreakDown);
                        this.barchartData = barChartData;
                        this.size="xs12";
                    } else {
                        this.isCountrydata = false;
                        this.displayMessage = true;
                    }
                    
                } else {
                    var query =` query getASNGeo { getASNGeo ( asn: "` + asn + `") { country_break_down } }`;
                    const ASNGeo = await API.graphql(graphqlOperation(query));
                    var baselineGeo = ASNGeo.data.getASNGeo[0];
                    console.log("asn geo below");
                    console.log(baselineGeo);
                    if (baselineGeo) {
                        if (baselineGeo.country_break_down) {
                            var doughnutDataCountry = this.createdoughnutDataCountry(baselineGeo.country_break_down);
                            this.doughnutData = doughnutDataCountry;
                            this.isEventData = false;
                            this.isCountrydata = true;
                            
                        }
                    } else {
                        this.displayMessage = true;
                        this.isCountrydata = false;
                    }
                    
                }
                this.loading = false;
            }
        },
        createdoughnutDataCountry: function (countryBD) {
            var data = { datasets: [{ data: [], backgroundColor: []}], labels: [] };
            //countryBD = JSON.parse(countryBD.replace(/'/g, '"'))
            countryBD = JSON.parse(countryBD)
            
            var countrykeys = Object.keys(countryBD);
            var country_keys_list = []
            for (var j = 0; j < countrykeys.length ; j++) {
                country_keys_list.push({
                    'code': countrykeys[j],
                    'prop': countryBD[countrykeys[j]]
                })
            }

            country_keys_list.sort(function(a, b) {
                var keyA = a.prop,
                    keyB = b.prop;
                // Compare the 2 dates
                if (keyA < keyB) return 1;
                if (keyA > keyB) return -1;
                return 0;
            });
            
            var additional_countries = []
            this.additional_countries = []

            for (var i = 0; i < country_keys_list.length; i=i+1){
                if (country_keys_list[i].prop > 0.005) {
                    if (country_keys_list[i].code == "") {
                        data.labels.push("Unknown")
                    } else {
                        data.labels.push(countryNames[country_keys_list[i].code])
                    }
                
                    data.datasets[0].data.push((country_keys_list[i].prop*1).toFixed(2))
                    if (i < this.distinct_colors.length) {
                        var color = this.distinct_colors[i]
                    } else {
                        var color = "hsla(" + ~~(360 * Math.random()) + "," +"50%,"+"50%,1)"
                    }
                    data.datasets[0].backgroundColor.push(color)
                } else {
                    additional_countries.push(countryNames[country_keys_list[i].code])
                }
            }

            if (additional_countries.length > 0) {
                console.log("additional countries")
                if (additional_countries.length == 1) {
                    this.additional_countries = additional_countries[0]
                } else {
                    this.additional_countries = additional_countries.join(", ")
                }
            } else {
                console.log("no additional countries")
            }

            return data


        },
        createBarChartData: function (allData) {
            var hijackImpactScoreDataList = ["h_0_30", "h_30_60", "h_60_99", "i_0_30", "i_30_60", "i_60_99"]
            var hijackImpactScoreDataComboList= ["hi_0_900", "hi_900_3600", "hi_3600_9801"]
            var anomolyTypeCountsList = ["Own_assert_count", "first_obs_count", "path_change_count", "restricted_dns_count",
                "bogon_count", "route_leak_count"]

            var hijackImpactScoreDataListNames = ["H 0-30", "H 30-60", "H 60-99", "I 0-30", "I 30-60", "I 60-99"]
            var hijackImpactScoreDataComboListNames= ["Combined 0-900", "Combined 900-3600", "Combined 3600-9801"]
            var anomolyTypeCountsListName = ["Ownership", "First Observation", "Path Change", "Restricted DNS",
                "Bogon", "Route Leak"]

            

            var hijackImpactScoreData = { 
                labels: ["Hijack vs Impact"], //"h_0_30", "h_30_60", "h_60_99", "i_0_30", "i_30_60", "i_60_99" 
                datasets: [] //allData.h_0_30, allData.h_30_60, allData.h_60_99, allData.i_0_30, allData.i_30_60, allData.i_60_99 
                }

            var hijackImpactScoreDataCombo = {
                labels: ["Combined Hijack vs Impact"],
                datasets: []
            }

            var anomolyTypeCounts ={
                labels: ["Anomaly Type"],
                datasets: []
            }
            
            for (var i=0; i <hijackImpactScoreDataList.length; i = i+1) {
                
                if ( i < 3 ) {
                    var scale = this.greenscale
                    var dataset =   {label: [hijackImpactScoreDataListNames[i]], data: [allData[hijackImpactScoreDataList[i]]], 
                    borderColor: scale[i], backgroundColor: scale[i]}
                } else {
                    var scale = this.bluescale
                    var dataset =   {label: [hijackImpactScoreDataListNames[i]], data: [allData[hijackImpactScoreDataList[i]]], 
                    borderColor: scale[i - 3], backgroundColor: scale[i - 3]}
                }
                hijackImpactScoreData.datasets.push(dataset)
            }

            for (var j=0; j <hijackImpactScoreDataComboList.length; j= j+1) {
                var scale = this.purplescale
                var dataset =   {label: [hijackImpactScoreDataComboListNames[j]], data: [allData[hijackImpactScoreDataComboList[j]]], 
                borderColor: scale[j], backgroundColor: scale[j]}
                hijackImpactScoreDataCombo.datasets.push(dataset)
            }

            for (var k=0; k <anomolyTypeCountsList.length; k= k+1) {
                var scale = this.col6
                var dataset =   {label: [anomolyTypeCountsListName[k]], data: [allData[anomolyTypeCountsList[k]]], 
                borderColor: scale[k], backgroundColor: scale[k]}
                anomolyTypeCounts.datasets.push(dataset)
            }

            var BarChartData = [hijackImpactScoreData, hijackImpactScoreDataCombo, anomolyTypeCounts]



            return BarChartData
            
        },
        sortPrefixCountry: function (countryJSON) {
            
            var keys = Object.keys(countryJSON)
            var countryString = ""
            var country_keys_list = []
            for (var j = 0; j < keys.length ; j++) {
                country_keys_list.push({
                    'code': keys[j],
                    'prop': countryJSON[keys[j]]
                })
            }

            country_keys_list.sort(function(a, b) {
                var keyA = a.prop,
                    keyB = b.prop;
                // Compare the 2 dates
                if (keyA < keyB) return 1;
                if (keyA > keyB) return -1;
                return 0;
            });
            for (var i =0; i< country_keys_list.length; i = i+1) {
                countryString = countryString + countryNames[country_keys_list[i].code] + ": " + country_keys_list[i].prop + "%"
                if (i != keys.length - 1 && keys.length > 1) {
                    countryString= countryString + ", "
                }
            }
            return countryString;
            
        }
    }

}
</script>

<style scoped>

    tbody tr:nth-of-type(even) {
      background-color: rgba(0, 0, 0, .05);
    }

    .v-table tr:hover:not(.v-table__expanded__content) {
        background-color: rgba(0, 0, 0, .4);
    }

</style>